import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Admin sees everything; staff sees only their own-data pages
const ADMIN_NAV = [
  { path: '/dashboard',            icon: '⊞', label: 'Dashboard',          section: 'core' },
  { path: '/pending-requests',     icon: '🔔', label: 'Pending Requests',   section: 'core' },
  { path: '/attendance',           icon: '✦', label: 'Attendance',         section: 'core' },
  { path: '/staff',                icon: '◈', label: 'Staff',              section: 'core' },
  { path: '/departments',          icon: '⬡', label: 'Departments',        section: 'core' },
  { path: '/leave',                icon: '◷', label: 'Leave',              section: 'hr' },
  { path: '/leave-balance',        icon: '◑', label: 'Leave Balance',      section: 'hr' },
  { path: '/overtime',             icon: '⊕', label: 'Overtime',           section: 'hr' },
  { path: '/holidays',             icon: '◉', label: 'Holidays',           section: 'hr' },
  { path: '/announcements',        icon: '◆', label: 'Announcements',      section: 'hr' },
  { path: '/grievances',           icon: '◎', label: 'Grievances',         section: 'hr' },
  { path: '/disciplinary',         icon: '⊘', label: 'Disciplinary',       section: 'hr' },
  { path: '/hr-reports',           icon: '📄', label: 'HR Documents',       section: 'hr' },
  { path: '/birthday-anniversary', icon: '🎂', label: 'Birthday / Anniv',   section: 'hr' },
  { path: '/helpdesk',             icon: '🎫', label: 'HR Helpdesk',        section: 'hr' },
  { path: '/salary',               icon: '₹', label: 'Salary',             section: 'payroll' },
  { path: '/salary-revisions',     icon: '↑', label: 'Salary Revisions',   section: 'payroll' },
  { path: '/pay-grades',           icon: '☰', label: 'Pay Grades',         section: 'payroll' },
  { path: '/loans',                icon: '◐', label: 'Loans & Advances',   section: 'payroll' },
  { path: '/reimbursements',       icon: '🧾', label: 'Reimbursements',     section: 'payroll' },
  { path: '/reports',              icon: '⊟', label: 'Attendance Reports', section: 'payroll' },
  { path: '/advanced-reports',     icon: '📊', label: 'Advanced Reports',   section: 'payroll' },
  { path: '/form16',               icon: '📋', label: 'Form 16',            section: 'payroll' },
  { path: '/statutory',            icon: '⚖', label: 'Statutory Compliance',section: 'payroll' },
  { path: '/insurance',            icon: '🛡️', label: 'Insurance',          section: 'insurance' },
  { path: '/transfers',            icon: '🔄', label: 'Transfers',          section: 'insurance' },
  { path: '/recruitment',          icon: '⊛', label: 'Recruitment',        section: 'workforce' },
  { path: '/onboarding',           icon: '⊙', label: 'Onboarding',         section: 'workforce' },
  { path: '/training',             icon: '⊠', label: 'Training',           section: 'workforce' },
  { path: '/performance',          icon: '★', label: 'Performance',        section: 'workforce' },
  { path: '/assets',               icon: '⊗', label: 'Assets',             section: 'ops' },
  { path: '/shifts',               icon: '⊜', label: 'Shifts',             section: 'ops' },
  { path: '/exit-management',      icon: '⊖', label: 'Exit Mgmt',          section: 'ops' },
  { path: '/chat',                 icon: '💬', label: 'Chat',               section: 'comms' },
  { path: '/notifications',        icon: '🔔', label: 'Notifications',      section: 'comms' },
  { path: '/policies',             icon: '⊡', label: 'Policies',           section: 'settings' },
  { path: '/permissions',          icon: '🔒', label: 'Permissions',        section: 'settings' },
  { path: '/company',              icon: '🏛', label: 'Company',            section: 'settings' },
];

const MANAGER_NAV = [
  { path: '/dashboard',       icon: '⊞', label: 'Dashboard',        section: 'home' },
  { path: '/manager-inbox',   icon: '👔', label: 'Manager Inbox',    section: 'home' },
  { path: '/attendance',      icon: '✦', label: 'Attendance',        section: 'home' },
  { path: '/leave',           icon: '◷', label: 'Leave Requests',    section: 'myteam' },
  { path: '/leave-balance',   icon: '◑', label: 'Leave Balance',     section: 'myteam' },
  { path: '/overtime',        icon: '⊕', label: 'Overtime',          section: 'myteam' },
  { path: '/grievances',      icon: '◎', label: 'Grievances',        section: 'myteam' },
  { path: '/disciplinary',    icon: '⊘', label: 'Disciplinary',      section: 'myteam' },
  { path: '/helpdesk',        icon: '🎫', label: 'HR Helpdesk',       section: 'myteam' },
  { path: '/reimbursements',  icon: '🧾', label: 'Reimbursements',    section: 'myteam' },
  { path: '/salary',          icon: '₹', label: 'My Salary',         section: 'mypay' },
  { path: '/loans',           icon: '◐', label: 'Loans & Advances',  section: 'mypay' },
  { path: '/training',        icon: '⊠', label: 'Training',          section: 'growth' },
  { path: '/performance',     icon: '★', label: 'Performance',       section: 'growth' },
  { path: '/assets',          icon: '⊗', label: 'Assets',            section: 'growth' },
  { path: '/chat',            icon: '💬', label: 'Chat',              section: 'comms' },
  { path: '/notifications',   icon: '🔔', label: 'Notifications',     section: 'comms' },
  { path: '/holidays',        icon: '◉', label: 'Holidays',          section: 'info' },
  { path: '/announcements',   icon: '◆', label: 'Announcements',     section: 'info' },
  { path: '/policies',        icon: '⊡', label: 'HR Policies',       section: 'info' },
];

const MANAGER_SECTIONS = {
  home:    { label: 'HOME',         defaultOpen: true,  color: '#60a5fa', icon: '⊞' },
  myteam:  { label: 'MY TEAM',      defaultOpen: true,  color: '#34d399', icon: '👥' },
  mypay:   { label: 'MY PAYROLL',   defaultOpen: true,  color: '#fbbf24', icon: '₹' },
  growth:  { label: 'GROWTH',       defaultOpen: false, color: '#a78bfa', icon: '★' },
  comms:   { label: 'COMMUNICATION',defaultOpen: false, color: '#ec4899', icon: '💬' },
  info:    { label: 'INFORMATION',  defaultOpen: false, color: '#94a3b8', icon: '◉' },
};

const STAFF_NAV = [
  { path: '/dashboard',       icon: '⊞', label: 'Dashboard',        section: 'home' },
  { path: '/attendance',      icon: '✦', label: 'My Attendance',    section: 'home' },
  { path: '/leave',           icon: '◷', label: 'My Leaves',        section: 'myhr' },
  { path: '/leave-balance',   icon: '◑', label: 'Leave Balance',    section: 'myhr' },
  { path: '/overtime',        icon: '⊕', label: 'Overtime',         section: 'myhr' },
  { path: '/grievances',      icon: '◎', label: 'Grievances',       section: 'myhr' },
  { path: '/disciplinary',    icon: '⊘', label: 'Disciplinary',     section: 'myhr' },
  { path: '/helpdesk',        icon: '🎫', label: 'HR Helpdesk',      section: 'myhr' },
  { path: '/reimbursements',  icon: '🧾', label: 'Reimbursements',   section: 'myhr' },
  { path: '/salary',          icon: '₹', label: 'My Salary',        section: 'mypay' },
  { path: '/loans',           icon: '◐', label: 'Loans & Advances', section: 'mypay' },
  { path: '/training',        icon: '⊠', label: 'My Training',      section: 'mygrowth' },
  { path: '/performance',     icon: '★', label: 'My Performance',   section: 'mygrowth' },
  { path: '/assets',          icon: '⊗', label: 'My Assets',        section: 'mygrowth' },
  { path: '/chat',            icon: '💬', label: 'Chat',             section: 'comms' },
  { path: '/notifications',   icon: '🔔', label: 'Notifications',    section: 'comms' },
  { path: '/holidays',        icon: '◉', label: 'Holidays',         section: 'info' },
  { path: '/announcements',   icon: '◆', label: 'Announcements',    section: 'info' },
  { path: '/policies',        icon: '⊡', label: 'HR Policies',      section: 'info' },
];

const ADMIN_SECTIONS = {
  core:       { label: 'CORE',            defaultOpen: true,  color: '#60a5fa', icon: '⊞' },
  hr:         { label: 'HR MANAGEMENT',   defaultOpen: true,  color: '#34d399', icon: '◈' },
  payroll:    { label: 'PAYROLL',         defaultOpen: true,  color: '#fbbf24', icon: '₹' },
  insurance:  { label: 'INSURANCE',       defaultOpen: false, color: '#06b6d4', icon: '🛡️' },
  workforce:  { label: 'WORKFORCE',       defaultOpen: false, color: '#a78bfa', icon: '⊛' },
  ops:        { label: 'OPERATIONS',      defaultOpen: false, color: '#fb923c', icon: '⊗' },
  comms:      { label: 'COMMUNICATION',   defaultOpen: false, color: '#ec4899', icon: '💬' },
  settings:   { label: 'SETTINGS',        defaultOpen: false, color: '#94a3b8', icon: '⊡' },
};

const STAFF_SECTIONS = {
  home:      { label: 'HOME',          defaultOpen: true,  color: '#60a5fa', icon: '⊞' },
  myhr:      { label: 'MY HR',         defaultOpen: true,  color: '#34d399', icon: '◷' },
  mypay:     { label: 'MY PAYROLL',    defaultOpen: true,  color: '#fbbf24', icon: '₹' },
  mygrowth:  { label: 'MY GROWTH',     defaultOpen: false, color: '#a78bfa', icon: '★' },
  comms:     { label: 'COMMUNICATION', defaultOpen: false, color: '#ec4899', icon: '💬' },
  info:      { label: 'INFORMATION',   defaultOpen: false, color: '#94a3b8', icon: '◉' },
};

const PAGE_TITLES = {
  '/dashboard': 'Dashboard', '/pending-requests': 'Pending Requests', '/manager-inbox': 'Manager Inbox', '/attendance': 'Attendance', '/staff': 'Staff Management',
  '/departments': 'Departments', '/leave': 'Leave Management', '/leave-balance': 'Leave Balances',
  '/overtime': 'Overtime', '/holidays': 'Holidays', '/announcements': 'Announcements',
  '/grievances': 'Grievances', '/disciplinary': 'Disciplinary Actions',
  '/salary': 'Salary & Payroll', '/salary-revisions': 'Salary Revisions',
  '/pay-grades': 'Pay Grades', '/loans': 'Loans & Advances', '/reports': 'Reports',
  '/recruitment': 'Recruitment', '/onboarding': 'Onboarding', '/training': 'Training',
  '/performance': 'Performance Reviews', '/assets': 'Asset Management',
  '/shifts': 'Shift Management', '/exit-management': 'Exit Management',
  '/policies': 'HR Policies', '/permissions': 'User Permissions', '/company': 'Company Settings',
  '/insurance': 'Insurance Management', '/transfers': 'Employee Transfers',
  '/hr-reports': 'HR Documents', '/birthday-anniversary': 'Birthdays & Anniversaries',
  '/helpdesk': 'HR Helpdesk', '/reimbursements': 'Reimbursement Claims',
  '/chat': 'Chat', '/notifications': 'Notifications',
  '/advanced-reports': 'Advanced Reports & Analytics',
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function ProfileDropdown({ user, onLogout, onClose, isAdmin }) {
  return (
    <div style={{
      position: 'absolute', top: 52, right: 0, zIndex: 200,
      background: '#fff', borderRadius: 16, width: 240,
      boxShadow: '0 8px 40px rgba(0,0,0,0.14)', border: '1px solid #f1f5f9',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '18px 18px 14px', background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', textAlign: 'center' }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%', margin: '0 auto 10px',
          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800, fontSize: 18,
          boxShadow: '0 4px 12px rgba(139,92,246,0.5)',
        }}>{getInitials(user?.username)}</div>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{user?.username}</div>
        <div style={{
          display: 'inline-block', marginTop: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
          background: user?.role === 'ADMIN' ? 'rgba(59,130,246,0.3)' : 'rgba(16,185,129,0.3)',
          color: user?.role === 'ADMIN' ? '#93c5fd' : '#6ee7b7',
          padding: '2px 10px', borderRadius: 20,
        }}>{user?.role}</div>
      </div>
      <div style={{ padding: '8px 0' }}>
        <div style={{ padding: '6px 14px 4px', fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1 }}>QUICK LINKS</div>
        {isAdmin && (
          <NavLink to="/company" onClick={onClose} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', textDecoration: 'none', color: '#374151', fontSize: 13 }}
            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <span>🏛</span> Company Settings
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/permissions" onClick={onClose} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', textDecoration: 'none', color: '#374151', fontSize: 13 }}
            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <span>🔒</span> User Permissions
          </NavLink>
        )}
        <NavLink to="/policies" onClick={onClose} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', textDecoration: 'none', color: '#374151', fontSize: 13 }}
          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <span>⊡</span> HR Policies
        </NavLink>
      </div>
      <div style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0' }}>
        <button onClick={onLogout} style={{
          width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 18px', background: 'none', border: 'none',
          color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}
          onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <span>⏻</span> Logout
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const navRef = useRef(null);
  const activeItemRef = useRef(null);

  const isAdmin   = user?.role === 'ADMIN';
  const isManager = user?.role === 'MANAGER';
  const NAV      = isAdmin ? ADMIN_NAV : isManager ? MANAGER_NAV : STAFF_NAV;
  const SECTIONS = isAdmin ? ADMIN_SECTIONS : isManager ? MANAGER_SECTIONS : STAFF_SECTIONS;

  // Determine which section the current route belongs to
  const activeSection = NAV.find(n => n.path === location.pathname)?.section;

  // Persist section open/close state in localStorage so it survives page navigation
  // (Layout remounts on every route change because it lives inside PrivateRoute)
  const STORAGE_KEY = `apms_nav_${user?.role || 'guest'}`;

  const [openSections, setOpenSections] = useState(() => {
    // Try to restore user's saved preferences
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge saved state with current section defaults (in case sections changed)
        const init = {};
        Object.entries(SECTIONS).forEach(([k, v]) => {
          init[k] = parsed[k] !== undefined ? parsed[k] : v.defaultOpen;
        });
        return init;
      }
    } catch {}
    // First time: use defaults
    const init = {};
    Object.entries(SECTIONS).forEach(([k, v]) => { init[k] = v.defaultOpen; });
    return init;
  });

  // Save to localStorage whenever sections change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(openSections)); } catch {}
  }, [openSections]);

  // When route changes, ONLY open the section containing the active page (keep others as-is)
  useEffect(() => {
    if (!activeSection) return;
    setOpenSections(prev => {
      // Only update if the section is currently closed — don't disturb others
      if (prev[activeSection]) return prev;
      return { ...prev, [activeSection]: true };
    });
    setTimeout(() => {
      if (activeItemRef.current && navRef.current) {
        activeItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 60);
  }, [location.pathname]);

  const handleLogout = () => {
    setProfileOpen(false);
    // Clear nav state on logout
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    logout();
    navigate('/login');
  };
  const pageTitle = PAGE_TITLES[location.pathname] || 'Dashboard';

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Toggle: open if closed, close if open — save immediately
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', 'Segoe UI', sans-serif", background: '#f0f4ff' }}>

      {/* Sidebar */}
      <aside style={{
        width: open ? 248 : 64, minHeight: '100vh', flexShrink: 0,
        background: 'linear-gradient(180deg, #0a1628 0%, #0d1f4a 40%, #1a237e 100%)',
        transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '4px 0 24px rgba(10,22,40,0.4)', position: 'relative', zIndex: 10,
      }}>

        {/* Logo */}
        <div style={{
          padding: open ? '20px 16px 16px' : '20px 10px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', alignItems: 'center', gap: 10,
          justifyContent: open ? 'space-between' : 'center', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 17, boxShadow: '0 4px 12px rgba(59,130,246,0.4)',
            }}>🏛️</div>
            {open && (
              <div>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 11, letterSpacing: 0.3, whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                  {company?.name?.trim() || 'APMS'}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 8, whiteSpace: 'nowrap', letterSpacing: 0.5 }}>PAYROLL SYSTEM</div>
              </div>
            )}
          </div>
          <button onClick={() => setOpen(!open)} style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)', cursor: 'pointer', borderRadius: 7,
            width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, flexShrink: 0,
          }}>{open ? '◀' : '▶'}</button>
        </div>

        {/* Nav — scrollable, maintains position */}
        <nav ref={navRef} style={{ flex: 1, padding: '8px 0', overflowY: 'auto', overflowX: 'hidden' }}>
          {Object.entries(SECTIONS).map(([sectionKey, sectionMeta], secIdx) => {
            const items = NAV.filter(n => n.section === sectionKey);
            const isOpen = openSections[sectionKey];
            const hasActive = items.some(i => i.path === location.pathname);
            const secColor = sectionMeta.color;

            return (
              <div key={sectionKey}>
                {/* Divider between sections */}
                {secIdx > 0 && open && (
                  <div style={{ margin: '4px 16px 0', height: 1, background: 'rgba(255,255,255,0.07)' }} />
                )}

                {/* Section header */}
                <div
                  onClick={() => open && toggleSection(sectionKey)}
                  style={{
                    padding: open ? '10px 12px 6px 14px' : '10px 0 6px',
                    display: 'flex', alignItems: 'center', justifyContent: open ? 'space-between' : 'center',
                    cursor: open ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  {open ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flex: 1 }}>
                      <div style={{
                        width: 3, height: 14, borderRadius: 2, flexShrink: 0,
                        background: hasActive ? secColor : 'rgba(255,255,255,0.15)',
                        transition: 'background 0.2s',
                      }} />
                      <span style={{
                        fontSize: 11, fontWeight: 800, letterSpacing: 1,
                        color: hasActive ? secColor : 'rgba(255,255,255,0.65)',
                        transition: 'color 0.2s',
                      }}>{sectionMeta.label}</span>
                      <span style={{
                        marginLeft: 'auto', fontSize: 8, fontWeight: 700,
                        color: hasActive ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
                      }}>{isOpen ? '▼' : '▶'}</span>
                    </div>
                  ) : (
                    <div style={{
                      width: 24, height: 24, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: hasActive ? `${secColor}22` : 'transparent',
                      fontSize: 11,
                    }}>{sectionMeta.icon}</div>
                  )}
                </div>

                {/* Items */}
                <div style={{
                  maxHeight: isOpen || !open ? 1000 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.28s ease',
                }}>
                  {items.map(item => {
                    const isActive = location.pathname === item.path;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        ref={isActive ? activeItemRef : null}
                        onMouseEnter={e => {
                          if (!isActive) {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                            e.currentTarget.style.color = '#fff';
                          }
                        }}
                        onMouseLeave={e => {
                          if (!isActive) {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'rgba(255,255,255,0.68)';
                          }
                        }}
                        style={{
                          display: 'flex', alignItems: 'center',
                          gap: 10, padding: open ? '8px 12px 8px 24px' : '9px',
                          justifyContent: open ? 'flex-start' : 'center',
                          color: isActive ? '#fff' : 'rgba(255,255,255,0.68)',
                          textDecoration: 'none', fontSize: 13, fontWeight: isActive ? 600 : 450,
                          background: isActive
                            ? `linear-gradient(90deg, ${secColor}30, ${secColor}08)`
                            : 'transparent',
                          borderLeft: isActive ? `3px solid ${secColor}` : '3px solid transparent',
                          borderRadius: isActive ? '0 8px 8px 0' : 0,
                          transition: 'background 0.15s, color 0.15s',
                          margin: '1px 0', whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{
                          fontSize: 13, flexShrink: 0, minWidth: 18, textAlign: 'center',
                          color: isActive ? secColor : 'inherit', transition: 'color 0.15s',
                        }}>{item.icon}</span>
                        {open && <span style={{ lineHeight: 1.2 }}>{item.label}</span>}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div style={{ height: 16 }} />
        </nav>
      </aside>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>

        {/* Top Bar */}
        <header style={{
          background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.06)',
          padding: '0 26px', height: 58,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 1px 12px rgba(0,0,0,0.05)', flexShrink: 0, position: 'relative', zIndex: 50,
        }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{pageTitle}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              background: user?.role === 'ADMIN' ? '#eff6ff' : '#f0fdf4',
              color: user?.role === 'ADMIN' ? '#2563eb' : '#16a34a',
              padding: '4px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700,
              border: `1px solid ${user?.role === 'ADMIN' ? '#bfdbfe' : '#bbf7d0'}`,
              letterSpacing: 0.5,
            }}>{user?.role}</div>

            <div ref={profileRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setProfileOpen(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: profileOpen ? '#f1f5f9' : 'transparent',
                  border: '1.5px solid ' + (profileOpen ? '#e2e8f0' : 'transparent'),
                  borderRadius: 12, padding: '5px 10px 5px 5px', cursor: 'pointer',
                }}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 800, fontSize: 12,
                  boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                }}>{getInitials(user?.username)}</div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{user?.username}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>Click to manage</div>
                </div>
                <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 2 }}>{profileOpen ? '▲' : '▼'}</span>
              </button>
              {profileOpen && <ProfileDropdown user={user} onLogout={handleLogout} onClose={() => setProfileOpen(false)} isAdmin={isAdmin} />}
            </div>
          </div>
        </header>

        {/* Page Content — scroll reset on route change */}
        <main
          key={location.pathname}
          style={{ flex: 1, overflowY: 'auto', padding: '22px 26px', background: '#f0f4ff' }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
